import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import type { OrgSecurityGroup, PageEditor } from "@/types/database";

// page_editors is a new table since this repo's generated Supabase types
// were last refreshed — accessed via `as any` table-name casts, same
// stopgap already used for org_domains/teams (see CLAUDE.md).
export function usePageEditors(pageId: string | undefined) {
  const getClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const { userId: currentUserId } = useAuth();
  const queryKey = ["page-editors", pageId] as const;

  const query = useQuery({
    queryKey,
    enabled: Boolean(pageId),
    queryFn: async (): Promise<PageEditor[]> => {
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("page_editors" as any)
        .select("*")
        .eq("page_id", pageId as string);
      if (error) throw error;
      return (data ?? []) as unknown as PageEditor[];
    },
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey });

  const addPersonEditor = useMutation({
    mutationFn: async (clerkUserId: string): Promise<void> => {
      if (!pageId) throw new Error("No page selected");
      if (!currentUserId) throw new Error("Not signed in");
      const supabase = await getClient();
      const { error } = await supabase.from("page_editors" as any).insert({
        page_id: pageId,
        clerk_user_id: clerkUserId,
        created_by_clerk_user_id: currentUserId,
      } as any);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Grants access to whoever currently belongs to the group (managed in
  // Scouting Ops Hub) — see docs/site-builder-overhaul.md's Phase E.
  const addGroupEditor = useMutation({
    mutationFn: async (securityGroupId: string): Promise<void> => {
      if (!pageId) throw new Error("No page selected");
      if (!currentUserId) throw new Error("Not signed in");
      const supabase = await getClient();
      const { error } = await supabase.from("page_editors" as any).insert({
        page_id: pageId,
        security_group_id: securityGroupId,
        created_by_clerk_user_id: currentUserId,
      } as any);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const removeEditor = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const supabase = await getClient();
      const { error } = await supabase.from("page_editors" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    editors: query.data ?? [],
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    saving: addPersonEditor.isPending || addGroupEditor.isPending || removeEditor.isPending,
    addPersonEditor: (clerkUserId: string) => addPersonEditor.mutateAsync(clerkUserId),
    addGroupEditor: (securityGroupId: string) => addGroupEditor.mutateAsync(securityGroupId),
    removeEditor: (id: string) => removeEditor.mutateAsync(id),
  };
}

// The Access tab's group picker — list_org_security_groups() is gated by
// has_unit_permission('website_content','manage') server-side (not
// app.is_unit_admin()), since unit_security_groups' own RLS is
// admin-only-read and this feature shouldn't require a full org admin.
export function useOrgSecurityGroups() {
  const getClient = useSupabaseClient();

  const query = useQuery({
    queryKey: ["org-security-groups"],
    queryFn: async (): Promise<OrgSecurityGroup[]> => {
      const supabase = await getClient();
      const { data, error } = await supabase.rpc("list_org_security_groups" as any);
      if (error) throw error;
      return (data ?? []) as unknown as OrgSecurityGroup[];
    },
  });

  return { groups: query.data ?? [], loading: query.isLoading };
}

// Org-wide website_content:manage is the primary gate everywhere in Studio
// (see useHasPermission) — this adds the narrower page-scoped grant on top
// of it, so an assigned page editor can manage that one page's blocks even
// without the broader permission. Person-based grants are checked
// client-side (page_editors rows are already small and org-member-
// readable); group-based grants go through the same RPC the blocks RLS
// itself uses (current_user_in_security_group), since
// unit_security_group_members isn't directly readable by a non-admin.
export function useIsPageEditor(pageId: string | undefined): { isPageEditor: boolean; isLoading: boolean } {
  const { userId } = useAuth();
  const getClient = useSupabaseClient();
  const { editors, loading } = usePageEditors(pageId);
  const groupIds = editors.filter((editor) => editor.security_group_id).map((editor) => editor.security_group_id as string);

  const groupQuery = useQuery({
    queryKey: ["page-editor-group-membership", pageId, groupIds],
    enabled: groupIds.length > 0,
    queryFn: async (): Promise<boolean> => {
      const supabase = await getClient();
      const results = await Promise.all(
        groupIds.map(async (groupId) => {
          const { data, error } = await supabase.rpc("current_user_in_security_group" as any, { target_group_id: groupId });
          if (error) throw error;
          return Boolean(data);
        })
      );
      return results.some(Boolean);
    },
  });

  const directMatch = Boolean(userId) && editors.some((editor) => editor.clerk_user_id === userId);

  return {
    isPageEditor: directMatch || Boolean(groupQuery.data),
    isLoading: loading || (groupIds.length > 0 && groupQuery.isLoading),
  };
}
